import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, EmptyState, Input } from "../components/UI";
import { TrendFeedCard } from "../components/TrendFeedCard";
import { SpendTrackerCard } from "../components/SpendTrackerCard";
import { toast } from "sonner";
import {
  Landmark, ShieldCheck, ExternalLink, Loader2, Search, CalendarDays, Vote, RefreshCw, Sparkles, Bell,
} from "lucide-react";

const usd = (n) => n == null ? "—" : "$" + new Intl.NumberFormat().format(Math.round(n));
const PARTY_COLORS = { Republican: "text-red-300 border-red-400/30 bg-red-400/10", Democratic: "text-sky-300 border-sky-400/30 bg-sky-400/10" };

export default function RaceIntelligence() {
  const navigate = useNavigate();
  const [cc, setCc] = useState(null);
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elections, setElections] = useState(null);
  const [eq, setEq] = useState({ q: "", state: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/political-os/command-center");
      setCc(data);
      if (data.entity) {
        const r = await api.get(`/political-os/entities/${data.entity.id}/race-intelligence`);
        setIntel(r.data);
      }
    } catch (e) { toast.error(errMsg(e, "Could not load race intelligence")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const searchElections = async () => {
    const { data } = await api.get(`/political-os/elections/search?q=${encodeURIComponent(eq.q)}&state=${encodeURIComponent(eq.state)}`);
    setElections(data.elections);
  };

  const attachRace = async (raceId) => {
    try {
      await api.post(`/political-os/entities/${cc.entity.id}/race`, { race_id: raceId });
      toast.success("Race attached");
      setElections(null);
      setLoading(true);
      load();
    } catch (e) { toast.error(errMsg(e, "Could not attach race")); }
  };

  const [filling, setFilling] = useState(false);
  const aiFillFinance = async () => {
    setFilling(true);
    try {
      const { data } = await api.post(`/political-os/entities/${cc.entity.id}/race-finance/ai`);
      toast.success(data.populated ? `AI found finance figures for ${data.populated} candidate${data.populated > 1 ? "s" : ""}` : "No reported figures found yet — try again after the next filing");
      if (data.populated) { const r = await api.get(`/political-os/entities/${cc.entity.id}/race-intelligence`); setIntel(r.data); }
    } catch (e) { toast.error(errMsg(e, "AI research failed — try again")); }
    finally { setFilling(false); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  if (!cc?.entity) {
    return (
      <div className="p-6 lg:p-8">
        <EmptyState icon={Landmark} title="No political entity selected"
          description="Tell TotalReach who you represent and which race you're in — Race Intelligence builds itself from official public data."
          action={<Btn onClick={() => navigate("/onboarding/political")} data-testid="setup-entity-btn"><Vote className="w-4 h-4" /> Set up your political entity</Btn>} />
      </div>
    );
  }

  const v = intel?.entity?.verification_summary;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="race-intelligence-page">
      <PageHeader eyebrow="Race Intelligence" title={intel?.entity?.name || cc.entity.name}
        subtitle="Election overview, candidates in this race, and publicly reported campaign finance — every fact carries its source."
        action={v && (
          <button onClick={() => v.score < 85 && navigate("/verify-campaign")} title={v.score < 85 ? "Finish verification" : "Verified"}
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${v.score >= 85 ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10 cursor-default" : "border-amber-400/40 text-amber-300 bg-amber-400/10 hover:bg-amber-400/20"}`} data-testid="verification-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> {v.status}{v.score < 85 ? " — verify now" : ""}
          </button>
        )} />

      {!intel?.race ? (
        <Card data-testid="race-picker-card">
          <div className="text-sm text-slate-300 mb-3">{intel?.message || "Select your election to unlock Race Intelligence."}</div>
          <div className="flex gap-2 mb-3">
            <Input value={eq.state} onChange={e => setEq(s => ({ ...s, state: e.target.value.toUpperCase() }))} placeholder="State (TX)" className="w-28" />
            <Input value={eq.q} onChange={e => setEq(s => ({ ...s, q: e.target.value }))} placeholder="Search elections…" className="flex-1" />
            <Btn variant="secondary" onClick={searchElections} data-testid="ri-election-search-btn"><Search className="w-4 h-4" /></Btn>
          </div>
          {elections && (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {elections.map(e => (
                <button key={e.id} onClick={() => attachRace(e.id)} data-testid={`ri-election-${e.id}`}
                  className="w-full text-left p-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-cyan-400/40 transition-colors">
                  <div className="text-sm text-white">{e.name}</div>
                  <div className="text-[11px] text-slate-500">{e.election_date || "Date TBD"} · via {e.source}</div>
                </button>
              ))}
              {elections.length === 0 && <div className="text-sm text-slate-500">No matches.</div>}
            </div>
          )}
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <Card className="lg:col-span-1 text-center flex flex-col items-center justify-center py-8" data-testid="countdown-card">
              <div className="font-display text-6xl font-bold bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">{intel.days_until ?? "—"}</div>
              <div className="text-xs uppercase tracking-widest text-slate-400 mt-2">Days Until Election Day</div>
              <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {intel.race.election_date || "TBD"}</div>
            </Card>
            <Card className="lg:col-span-3" data-testid="election-overview-card">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Election overview</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                <Ov k="Election" v={intel.race.name} />
                <Ov k="Type" v={(intel.election?.election_type || "general") + " election"} />
                <Ov k="Level" v={intel.race.level || "federal"} />
                <Ov k="Office" v={intel.race.office_label || intel.race.office} />
                <Ov k="State / District" v={`${intel.race.state || "—"}${intel.race.district && intel.race.district !== "00" ? ` · District ${intel.race.district}` : ""}`} />
                <Ov k="Source" v={intel.election?.source || "TotalReach Election Registry"} link={intel.election?.source_url} />
              </div>
            </Card>
          </div>

          <Card padded={false} data-testid="race-candidates-card">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">Candidates in this race <span className="text-slate-500 text-sm font-normal">({intel.candidates.length})</span></h3>
              <div className="flex items-center gap-2">
                {intel.candidates.some(c => !c.finance) && (
                  <button onClick={aiFillFinance} disabled={filling} data-testid="ai-fill-finance-btn"
                    className="text-[10px] px-2.5 py-1 rounded-full bg-fuchsia-400/10 border border-fuchsia-400/30 text-fuchsia-200 hover:bg-fuchsia-400/20 disabled:opacity-50 inline-flex items-center gap-1">
                    {filling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Fill missing with AI
                  </button>
                )}
                <span className="text-[10px] uppercase tracking-wider text-slate-500">Publicly reported data</span>
              </div>
            </div>
            <div className="divide-y divide-white/5">
              {intel.candidates.map(c => (
                <div key={c.id} className={`px-5 py-4 flex flex-wrap items-center gap-4 ${c.is_yours ? "bg-cyan-400/[0.04]" : ""}`} data-testid={`race-candidate-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white">{c.name}</span>
                      {c.is_yours && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" data-testid="your-candidate-badge">Your candidate</span>}
                      <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${PARTY_COLORS[c.party] || "text-slate-400 border-white/15"}`}>{c.party || "—"}</span>
                      <span className="text-[10px] text-slate-500">{c.incumbent_challenge}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">FEC {c.fec_candidate_id || "—"}{c.first_file_date ? ` · first filed ${c.first_file_date}` : ""}</div>
                  </div>
                  {c.finance ? (
                    <div className="flex gap-5 text-right">
                      <Fin k="Receipts" v={usd(c.finance.receipts)} />
                      <Fin k="Spent" v={usd(c.finance.disbursements)} />
                      <Fin k="Cash on hand" v={usd(c.finance.cash_on_hand)} />
                      {c.finance.ai ? (
                        <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/10 border border-fuchsia-400/30 text-fuchsia-200 self-center" title={c.finance.note || "Figures from AI web research"} data-testid={`finance-ai-badge-${c.id}`}>AI-researched</span>
                      ) : (
                        <a href={c.finance.source_url} target="_blank" rel="noreferrer" title={`${c.finance.source} · through ${c.finance.coverage_end_date || "last filing"}`}
                          className="text-slate-600 hover:text-cyan-300 self-center" data-testid={`finance-source-${c.id}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  ) : (
                    <span className="text-[11px] text-slate-600">No public finance data yet</span>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card data-testid="sources-card">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Sources & provenance</div>
            <div className="space-y-1.5">
              {intel.sources.map((s, i) => (
                <div key={i} className="text-xs text-slate-400 flex items-start gap-2">
                  <ExternalLink className="w-3 h-3 mt-0.5 text-cyan-300/60 shrink-0" />
                  <span><a href={s.source_url} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">{s.source}</a>{s.note ? ` — ${s.note}` : ""}</span>
                </div>
              ))}
              <div className="text-[10px] text-slate-600 pt-1 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Generated {intel.generated_at?.slice(0, 16).replace("T", " ")} UTC · finance cached up to 24h · figures reflect the last public filing period, not real time</div>
            </div>
          </Card>
          <TrendFeedCard />
          <SpendTrackerCard />
          <AlertThresholdCard />
        </>
      )}
    </div>
  );
}

const Ov = ({ k, v, link }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
    <div className="text-white mt-0.5 capitalize">{link ? <a href={link} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline normal-case">{v}</a> : v}</div>
  </div>
);

const Fin = ({ k, v }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{k}</div>
    <div className="text-sm text-white font-mono">{v}</div>
  </div>
);

const AlertThresholdCard = () => {
  const [thr, setThr] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/political-os/finance-alert-threshold").then(r => setThr(r.data)).catch(() => {});
  }, []);
  if (!thr) return null;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/political-os/finance-alert-threshold",
        { min_delta_usd: Number(thr.min_delta_usd), min_pct: Number(thr.min_pct) });
      toast.success("Finance alert threshold saved");
    } catch (e) { toast.error(errMsg(e, "Could not save threshold")); }
    finally { setSaving(false); }
  };
  return (
    <Card data-testid="alert-threshold-card">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4 text-amber-300" />
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Finance alert threshold</div>
      </div>
      <p className="text-xs text-slate-500 mb-3">Alert my team when an opponent's cash on hand jumps by at least both of these:</p>
      <div className="flex flex-wrap items-end gap-3">
        <Input label="Minimum jump ($)" type="number" min="0" className="w-40" value={thr.min_delta_usd}
          onChange={(e) => setThr({ ...thr, min_delta_usd: e.target.value })} data-testid="threshold-delta-input" />
        <Input label="Minimum jump (%)" type="number" min="1" max="500" className="w-36" value={thr.min_pct}
          onChange={(e) => setThr({ ...thr, min_pct: e.target.value })} data-testid="threshold-pct-input" />
        <Btn variant="secondary" onClick={save} disabled={saving} data-testid="threshold-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Save
        </Btn>
      </div>
    </Card>
  );
};
